import { IconChevronChevronRight } from '@doltech/icons/IconChevronChevronRight';
import { Skeleton } from 'antd';
import { isNil, range } from 'lodash';
import * as React from 'react';
import styled from 'styled-components';
import { Cell, Grid, IGridProps } from 'styled-css-grid';

import { deviceBreakpoints, useDeviceDetect } from '../../../../../hooks/useDeviceQuery.hook';
import { Button } from '../../../../ButtonV2/ButtonV2';
import { colorsV2 } from '../../../../colors-v2';
import { Box } from '../../../../Common/BoxV2';
import { Typography } from '../../../../DesignSystemV2/Typography';
import { LineDivider, LineDividerProps } from '../../../../Divider/LineDivider';

export interface WithAnchor {
  anchor?: string;
}

export interface ReviewLayoutProps extends WithAnchor {
  metaLeft: React.ReactNode;
  metaRight?: React.ReactNode;
  heading: React.ReactNode;
  reviews: {
    key: string;
    heading?: React.ReactNode;
    content: React.ReactNode[];
  }[];
}

export const ReviewLayout = (props: ReviewLayoutProps) => {
  const { metaLeft, metaRight, heading, reviews, anchor } = props;

  return (
    <Container contentEditable={false}>
      <Metadata contentEditable={false}>
        {metaLeft}
        {isNil(metaRight) ? null : metaRight}
      </Metadata>

      <Box contentEditable={false} mt={[16, 24]} mb="8px">
        {heading}
      </Box>

      {reviews.map((review, idx) => {
        const isNotLast = idx < reviews.length - 1;
        return (
          <div key={review.key}>
            {review.heading ? (
              <Box contentEditable={false} mb={16}>
                {review.heading}
              </Box>
            ) : null}

            <ReviewContentLayout>{review.content}</ReviewContentLayout>
            {isNotLast && <LineDivider contentEditable={false} space={24} size={4} />}
          </div>
        );
      })}

      {anchor && (
        <Box contentEditable={false} mt={['16px', '24px']}>
          <a href={`#${anchor}`} title="Xem chi tiết" style={{ textDecoration: 'none' }}>
            <Button.Link
              iconRight={<IconChevronChevronRight />}
              style={{ margin: '0 auto', display: 'block', color: colorsV2.blue100 }}
            >
              <Typography.Text variant="semi-bold/14">Xem chi tiết</Typography.Text>
            </Button.Link>
          </a>
        </Box>
      )}
    </Container>
  );
};

/** Review layout
The left is: original position + center position at index
Which:
 - original position: is position of the VerticalLine should placed at the right of nth item

       original p.   original position
 item 1|       item 2|

- Each original position is calculated by: percentage at index * full width without gaps + total previous gaps

       original p.   original position
 item 1|[24px ]item 2|[24px ]item3|

 the original of 2nd item should be: 50% of full width without gap (because 2nd item) + 24px (total previous gap)
*/
const ReviewContentLayout = (props) => {
  const { children, columns = 2, columnGap = 48, rowGap = 32 } = props;
  const total = React.Children.count(children);
  const { isMobile } = useDeviceDetect();

  const gridConfig: IGridProps = React.useMemo(() => {
    if (isMobile) {
      return {
        columns: 1,
        columnGap: '0px',
        rowGap: '0px',
        alignContent: 'stretch',
        justifyContent: 'stretch',
      };
    }

    return {
      columns,
      columnGap: `${columnGap}px`,
      rowGap: `${rowGap}px`,
      alignContent: 'stretch',
      justifyContent: 'stretch',
      style: { position: 'relative' },
    };
  }, [columnGap, columns, isMobile, rowGap]);

  return (
    <Grid contentEditable={false} {...gridConfig}>
      {React.Children.map(children, (child, idx) => {
        const isNotLast = idx < total - columns + (isMobile ? 1 : 0);

        return (
          <Cell contentEditable={false} style={{ position: 'relative' }}>
            {child}
            {isNotLast ? (
              <HorizontalLine
                contentEditable={false}
                size={2}
                space={isMobile ? 12 : 0}
                bottom={-rowGap / 2}
              />
            ) : null}
          </Cell>
        );
      })}

      {!isMobile &&
        range(columns - 1).map((_, i) => {
          const percentageAtIndex = `${((i + 1) * 100) / columns} / 100`;
          const fullWidthWithoutGaps = `calc(100% - ${columnGap * (columns - 1)}px)`;
          const totalPreviousGaps = `${i * columnGap}px`;
          const original = `${percentageAtIndex} * ${fullWidthWithoutGaps} + ${totalPreviousGaps}`;

          return (
            <VerticalLine
              contentEditable={false}
              key={i.toString()}
              space={0}
              directional="vertical"
              left={`calc(${original}  + ${columnGap / 2}px)`}
            />
          );
        })}
    </Grid>
  );
};

const Container = styled(Box)`
  padding: 12px;
  border: 1px solid ${colorsV2.black10};
  border-radius: 12px;
  background: ${colorsV2.gray5};

  @media screen and ${deviceBreakpoints.fromTablet} {
    padding: 16px 24px;
  }
`;

const HorizontalLine = styled(LineDivider)<LineDividerProps & { bottom: number }>`
  @media screen and ${deviceBreakpoints.fromTablet} {
    position: absolute;
    width: 100%;
    left: 0;
    bottom: ${(p) => p.bottom}px;
  }
`;

const VerticalLine = styled(LineDivider)<{ left: string }>`
  @media screen and ${deviceBreakpoints.fromTablet} {
    position: absolute;
    height: 100%;
    top: 0;
    left: ${(p) => p.left};
  }
`;

const Metadata = styled(Grid)`
  grid-template-columns: 1fr;
  gap: 4px;

  @media ${deviceBreakpoints.fromTablet} {
    grid-template-columns: 212px minmax(0, 1fr);
    gap: 24px;
  }
`;

// =================================================================================
// Skeleton
// =================================================================================

export const ReviewLayoutSkeleton = ({ rightMeta = true }) => {
  return (
    <ReviewLayout
      metaLeft={<Skeleton.Image style={{ width: 252 }} />}
      metaRight={
        rightMeta ? (
          <Box contentEditable={false} mx="20px">
            <Skeleton title={{ width: '100%' }} paragraph={{ rows: 1 }} active />
          </Box>
        ) : null
      }
      heading={<Skeleton title={{ width: '100%' }} paragraph={{ rows: 1 }} active />}
      reviews={[
        {
          key: '1',
          heading: null,
          content: Array.from({ length: 4 }).map((content, i) => (
            <Skeleton title={{ width: '100%' }} paragraph={{ rows: 0 }} active key={i} />
          )),
        },
      ]}
    />
  );
};
